import * as React from "react";
import { useState, useEffect, Fragment } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Table,
    Row,
    Col,
    Button,
    FormGroup,
    Label,
    Input,
    Spinner,
    Badge,
} from "reactstrap";
import { Download, Filter, Search, CheckSquare, Square, RefreshCw, Calendar, Target, FileText } from "react-feather";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs";
import { AesDecrypt, AesEncrypt, keyPromise } from "../../../Service/crypto";
import Swal from "sweetalert2";
import api from "../../../api";

interface FilterOption {
    id: string;
    name: string;
}

interface ReportRow {
    [key: string]: any;
}

const ReportBuilder = () => {
    // Filter Options State
    const [clientsList, setClientsList] = useState<FilterOption[]>([]);
    const [insurancesList, setInsurancesList] = useState<FilterOption[]>([]);

    // Selected Filters State
    const [selectedClient, setSelectedClient] = useState<string>("all");
    const [selectedInsurance, setSelectedInsurance] = useState<string>("all");
    const getPastWeekDate = () => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split("T")[0];
    };

    const getCurrentDate = () => {
        return new Date().toISOString().split("T")[0];
    };

    const [startDate, setStartDate] = useState<string>(getPastWeekDate());
    const [endDate, setEndDate] = useState<string>(getCurrentDate());
    const [searchVal, setSearchVal] = useState<string>("");

    // Columns to include in the report
    const [columns, setColumns] = useState<{ key: string, label: string, selected: boolean }[]>([]);

    // Table & Pagination States
    const [reportData, setReportData] = useState<ReportRow[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [loading, setLoading] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState<boolean>(false);

    // Fetch Filter Lists on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const key = await keyPromise;
                const encParams = await AesEncrypt(key, JSON.stringify({}));
                const response = await api.fetch("api/reportbuilder/get_filters", {
                    method: "POST",
                    body: JSON.stringify({ EncParams: encParams }),
                });
                const encryptedData = await response.json();
                const decryptedData = await AesDecrypt(key, encryptedData);
                if (decryptedData) {
                    setClientsList(decryptedData.clients || []);
                    setInsurancesList(decryptedData.insurances || []);

                    if (decryptedData.columns && Array.isArray(decryptedData.columns)) {
                        const dynamicCols = decryptedData.columns.map((colObj: any) => {
                            const colName = typeof colObj === "string" ? colObj : colObj.name;
                            const isSelected = typeof colObj === "string" ? true : colObj.show;
                            return {
                                key: colName,
                                label: colName,
                                selected: isSelected
                            };
                        });
                        setColumns(dynamicCols);
                    }
                }
            } catch (error: any) {
                console.error("Error fetching filters:", error);
            }
        };
        fetchFilters();
    }, []);

    // Fetch Custom Report Data
    const generateReport = async (resetPage = false) => {
        setLoading(true);
        setHasSearched(true);
        const currentPage = resetPage ? 1 : page;
        if (resetPage) setPage(1);

        try {
            const key = await keyPromise;
            const payload = {
                client_id: selectedClient,
                insurance_name: selectedInsurance,
                startDate: startDate,
                endDate: endDate,
                q: searchVal,
                page: currentPage,
                perPage: perPage,
            };

            const encParams = await AesEncrypt(key, JSON.stringify(payload));
            const response = await api.fetch("api/reportbuilder/get_report", {
                method: "POST",
                body: JSON.stringify({ EncParams: encParams }),
            });

            const encryptedResult = await response.json();
            const decryptedResult = await AesDecrypt(key, encryptedResult);

            if (decryptedResult) {
                setReportData(decryptedResult.data || []);
                setTotalRecords(decryptedResult.total || 0);
            }
        } catch (error: any) {
            console.error("Error generating report:", error);
            Swal.fire("Error", "Failed to retrieve report data", "error");
        } finally {
            setLoading(false);
        }
    };

    // Trigger report generation on filter load or pagination changes
    useEffect(() => {
        if (hasSearched) {
            generateReport();
        }
    }, [page, perPage]);

    // Toggle dynamic column selection
    const toggleColumn = (index: number) => {
        const updated = [...columns];
        updated[index].selected = !updated[index].selected;
        setColumns(updated);
    };

    // Toggle All Columns
    const toggleAllColumns = (select: boolean) => {
        const updated = columns.map((col) => ({ ...col, selected: select }));
        setColumns(updated);
        if (!select) {
            setReportData([]);
            setTotalRecords(0);
            setHasSearched(false);
        }
    };

    // Export Custom Excel
    const handleExport = async () => {
        const selectedColLabels = columns
            .filter((col) => col.selected)
            .map((col) => col.label);

        if (selectedColLabels.length === 0) {
            Swal.fire("Information", "Please select at least one column to export.", "info");
            return;
        }

        setExporting(true);
        try {
            const key = await keyPromise;
            const payload = {
                client_id: selectedClient,
                insurance_name: selectedInsurance,
                startDate: startDate,
                endDate: endDate,
                q: searchVal,
                columns: selectedColLabels,
            };

            const encParams = await AesEncrypt(key, JSON.stringify(payload));
            const response = await api.fetch("api/reportbuilder/export_report", {
                method: "POST",
                body: JSON.stringify({ EncParams: encParams }),
            });

            const encryptedResult = await response.json();
            const decryptedResult = await AesDecrypt(key, encryptedResult);

            if (decryptedResult && decryptedResult.excel_file) {
                const byteCharacters = atob(decryptedResult.excel_file);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = decryptedResult.filename || "Custom_Report.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                Swal.fire({
                    title: "Success",
                    text: "Your custom Excel report has been downloaded successfully!",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire("Error", decryptedResult.error || "No data available to export.", "warning");
            }
        } catch (error: any) {
            console.error("Export Error:", error);
            Swal.fire("Error", "An error occurred while generating Excel sheet.", "error");
        } finally {
            setExporting(false);
        }
    };

    const selectedCols = columns.filter((col) => col.selected);

    // Render Table Value dynamically based on key
    const renderCellValue = (row: ReportRow, key: string) => {
        const val = row[key as keyof ReportRow];
        if (key === "created_at" && val) {
            return new Date(val).toLocaleString();
        }
        return val || "N/A";
    };

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Report Builder" title="Custom Reports" />

            {/* Main Filter & Config Panel */}
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-4">
                <CardHeader className="bg-white py-4 border-bottom-0 pb-0 d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill" style={{ backgroundColor: '#eef8f6' }}>
                            <Target size={14} color="#1abc9c" />
                            <span className="fw-bold fs-8 text-uppercase" style={{ color: '#1abc9c', letterSpacing: '0.5px' }}>FILTERS</span>
                        </div>
                    </div>
                </CardHeader>

                <CardBody className="p-4 bg-white">
                    <Row className="g-3">
                        {/* Client Filter */}
                        <Col md={3}>
                            <FormGroup>
                                <Label className="fw-bold text-muted text-uppercase fs-8 mb-2" style={{ letterSpacing: '0.5px' }}>Client Name</Label>
                                <Input
                                    type="select"
                                    value={selectedClient}
                                    onChange={(e) => setSelectedClient(e.target.value)}
                                    className="form-select border-2 rounded-3 py-2"
                                >
                                    <option value="all">All Clients</option>
                                    {clientsList.map((client) => (
                                        <option key={client.id} value={client.id}>
                                            {client.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>

                        {/* Insurance Filter */}
                        <Col md={3}>
                            <FormGroup>
                                <Label className="fw-bold text-muted text-uppercase fs-8 mb-2" style={{ letterSpacing: '0.5px' }}>Insurance Name</Label>
                                <Input
                                    type="select"
                                    value={selectedInsurance}
                                    onChange={(e) => setSelectedInsurance(e.target.value)}
                                    className="form-select border-2 rounded-3 py-2"
                                >
                                    <option value="all">All Insurances</option>
                                    {insurancesList.map((ins) => (
                                        <option key={ins.id} value={ins.name}>
                                            {ins.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>

                        {/* Date Range Filters */}
                        <Col md={3}>
                            <FormGroup>
                                <Label className="fw-bold text-muted text-uppercase fs-8 mb-2" style={{ letterSpacing: '0.5px' }}>Start Date</Label>
                                <div className="position-relative">
                                    <Input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="form-control border-2 rounded-3 py-2 ps-3"
                                    />
                                </div>
                            </FormGroup>
                        </Col>

                        <Col md={3}>
                            <FormGroup>
                                <Label className="fw-bold text-muted text-uppercase fs-8 mb-2" style={{ letterSpacing: '0.5px' }}>End Date</Label>
                                <div className="position-relative">
                                    <Input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="form-control border-2 rounded-3 py-2 ps-3"
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Dynamic Column Selector Grid */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
                            <Label className="fw-bold text-dark mb-0 fs-7">Choose Column Fields</Label>
                            <div className="d-flex gap-2">
                                <Button
                                    size="sm"
                                    color="white"
                                    onClick={() => toggleAllColumns(true)}
                                    className="rounded-pill fs-8 fw-semibold px-3 border"
                                    style={{ color: '#495057' }}
                                >
                                    Select All
                                </Button>
                                <Button
                                    size="sm"
                                    color="white"
                                    onClick={() => toggleAllColumns(false)}
                                    className="rounded-pill fs-8 fw-semibold px-3 border"
                                    style={{ color: '#e74c3c', borderColor: '#f5c6cb' }}
                                >
                                    Clear All
                                </Button>
                            </div>
                        </div>

                        <Row className="row-cols-2 row-cols-md-4 row-cols-lg-6 g-2">
                            {columns.map((col, index) => (
                                <Col key={col.key}>
                                    <div
                                        onClick={() => toggleColumn(index)}
                                        className="d-flex align-items-center p-2 rounded-2 border cursor-pointer"
                                        style={{
                                            transition: "all 0.15s ease-in-out",
                                            borderColor: col.selected ? '#1abc9c' : '#e0e0e0',
                                            backgroundColor: '#fff'
                                        }}
                                    >
                                        {col.selected ? (
                                            <CheckSquare size={16} className="me-2" style={{ color: '#1abc9c' }} />
                                        ) : (
                                            <Square size={16} className="me-2" style={{ color: '#ced4da' }} />
                                        )}
                                        <span
                                            className="fs-8 fw-semibold text-truncate"
                                            style={{ color: col.selected ? '#1abc9c' : '#6c757d' }}
                                        >
                                            {col.label}
                                        </span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-4">
                        <Button
                            color="white"
                            onClick={handleExport}
                            disabled={exporting || loading || selectedCols.length === 0}
                            className="px-4 py-2 rounded-2 fw-semibold border d-flex align-items-center"
                            style={{ borderColor: '#e0e0e0', color: '#495057' }}
                        >
                            {exporting ? (
                                <Spinner size="sm" className="me-2" />
                            ) : (
                                <Download size={16} className="me-2" />
                            )}
                            Export to Excel
                        </Button>
                        <Button
                            onClick={() => generateReport(true)}
                            disabled={loading || selectedCols.length === 0}
                            className="px-4 py-2 rounded-2 fw-semibold shadow-sm d-flex align-items-center text-white border-0"
                            style={{ backgroundColor: selectedCols.length === 0 ? '#cccccc' : '#1abc9c' }}
                        >
                            {loading ? (
                                <Spinner size="sm" className="me-2" />
                            ) : (
                                <FileText size={16} className="me-2" />
                            )}
                            Generate Report
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* Preview Table Card */}
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-5">
                <CardHeader className="bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <h6 className="mb-0 fw-bold text-dark">Report Live Preview</h6>
                        <div className="ms-3 px-2 py-1 rounded-pill fs-8 fw-semibold" style={{ backgroundColor: '#eef8f6', color: '#1abc9c' }}>
                            {totalRecords} Records
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <div className="position-relative">
                            <Input
                                type="text"
                                placeholder="Search patient, client..."
                                value={searchVal}
                                onChange={(e) => setSearchVal(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && generateReport(true)}
                                className="form-control border-2 rounded-pill py-1 px-3 pe-5"
                                style={{ width: "250px" }}
                            />
                            <Search
                                size={16}
                                onClick={() => generateReport(true)}
                                className="position-absolute end-0 top-50 translate-middle-y me-3 text-muted cursor-pointer"
                            />
                        </div>
                    </div>
                </CardHeader>

                <CardBody className="p-0">
                    <div className="table-responsive">
                        <Table hover striped className="mb-0 align-middle">
                            <thead className="bg-light text-muted text-uppercase fs-8 border-bottom">
                                <tr>
                                    {selectedCols.length === 0 ? (
                                        <th className="py-3 text-center px-3 border-0">No columns selected</th>
                                    ) : (
                                        selectedCols.map((col) => (
                                            <th key={col.key} className="py-3 text-start px-3 border-0" style={{ letterSpacing: '0.5px' }}>
                                                {col.label}
                                            </th>
                                        ))
                                    )}
                                </tr>
                            </thead>
                            <tbody className="fs-7">
                                {loading ? (
                                    <tr>
                                        <td colSpan={Math.max(selectedCols.length, 1)} className="text-center py-5">
                                            <Spinner color="primary" />
                                            <div className="mt-2 text-muted">Loading live report data...</div>
                                        </td>
                                    </tr>
                                ) : selectedCols.length === 0 ? null : reportData.length === 0 ? (
                                    <tr>
                                        <td colSpan={Math.max(selectedCols.length, 1)} className="text-center py-5 text-muted">
                                            No records match the selected filters. Click "Generate Report" to search.
                                        </td>
                                    </tr>
                                ) : (
                                    reportData.map((row, idx) => (
                                        <tr key={idx}>
                                            {selectedCols.map((col) => (
                                                <td key={col.key} className="py-3 text-start px-3">
                                                    {renderCellValue(row, col.key)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Elegant Pagination Footer */}
                    {totalRecords > 0 && (
                        <div className="p-3 border-top d-flex justify-content-between align-items-center bg-light-footer">
                            <div className="text-secondary fs-7">
                                Showing <span className="fw-semibold">{(page - 1) * perPage + 1}</span> to{" "}
                                <span className="fw-semibold">{Math.min(page * perPage, totalRecords)}</span> of{" "}
                                <span className="fw-semibold">{totalRecords}</span> records
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="select"
                                    value={perPage}
                                    onChange={(e) => {
                                        setPerPage(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="form-select border-2 rounded-3 py-1 fs-7"
                                    style={{ width: "80px" }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </Input>
                                <div className="d-flex gap-1">
                                    <Button
                                        size="sm"
                                        color="light"
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                        className="border"
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        disabled={page * perPage >= totalRecords}
                                        onClick={() => setPage(page + 1)}
                                        className="shadow-sm"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>
        </Fragment>
    );
};

export default ReportBuilder;